import { useSelector } from "react-redux";
import { useEffect, useRef } from "react";
import PropertyDetails from "@/components/pdp";
import { trackPageMount } from "@/utils/analytics";
import {
  generatePropertyDetailSchema,
  injectSchema,
  removeSchema,
} from "@/utils/schema";
import { getPropertyType } from "@/utils/propCategory";
import { generatePDPMeta } from "@/utils/meta";
import { useLocation } from "react-router";
import { Helmet } from "react-helmet-async";
import { captureAttribution, consumeInternalNavigation } from "@/utils/attributionCapture";
import { trackCategoryPageView, getCategoryFromPropCategory } from "@/utils/categoryPageTrackingService";

const PdpPage = () => {
  const { pathname } = useLocation();
  const pdpInitData = useSelector(
    (state) => state.pdp.pdpInitData?.propertyDetails,
  );
  const fullPropertyData = useSelector((state) => state.pdp.pdpInitData);
  const user = useSelector((state) => state.user);
  const hasTracked = useRef(false);
  const trackedPathname = useRef(null);
  // Capture internal navigation flag immediately on mount (before pdpInitData loads)
  const isInternalNav = useRef(consumeInternalNavigation());
  const meta = generatePDPMeta({pdpInitData, pathname})

  // Reset tracking flag when pathname changes (navigating to a different PDP)
  useEffect(() => {
    if (pathname !== trackedPathname.current) {
      hasTracked.current = false;
    }
  }, [pathname]);

  useEffect(() => {
    if (pdpInitData && !hasTracked.current) {
      // Verify this pdpInitData belongs to the current page
      // Handle both normal URLs (/bank-owned-home/123-...) and preview URLs (/bank-owned-home/preview/123-...)
      const pathSegments = pathname.split('/');
      const idSegment = pathSegments.includes('preview') ? pathSegments[3] : pathSegments[2];
      const urlPropertyId = idSegment?.split('-')[0];
      const dataPropertyId = String(pdpInitData?.propertyId || pdpInitData?.listingId || '');

      // Skip if the data is stale (belongs to a previous property)
      if (urlPropertyId && dataPropertyId && !dataPropertyId.includes(urlPropertyId) && !urlPropertyId.includes(dataPropertyId)) {
        return;
      }

      // Capture traffic attribution on PDP entry
      captureAttribution();


      // Track category landing page view (fires once per page load)
      const status = pdpInitData?.commonAttributes?.status;
      const isPreview = status === 'PACT' || status === 'PACT_NON_COMMERCIAL';
      let categoryType = getCategoryFromPropCategory(
        pdpInitData?.commonAttributes?.propCategory
      );
      if (categoryType && isPreview) {
        categoryType = `${categoryType},propertyStatus=${status}`;
      }
      if (categoryType) {
        trackCategoryPageView({
          categoryType,
          pageName: 'PDP',
          propertyId: pdpInitData?.propertyId || null,
          isInternal: isInternalNav.current,
        });
      }

      // Fire GA4/GTM page view — always fires on PDP load (independent of categoryType)
      trackPageMount({
        eventName: "pdp_view",
        eventSource: "PDP NON-COMMERCIAL",
        eventCategory: "NEW-PDP",
        listingId: pdpInitData?.listingId,
        property_type: getPropertyType(pdpInitData?.commonAttributes?.propCategory),
        seller_id: pdpInitData?.sellerId,
        eventLabel: `Category: ${categoryType || 'N/A'} | Property id: ${pdpInitData?.propertyId || ''}`,
      });

      hasTracked.current = true;
      trackedPathname.current = pathname;
    }
  }, [pdpInitData, pathname]);

  useEffect(() => {
    if (fullPropertyData) {
      const schemas = generatePropertyDetailSchema(fullPropertyData);
      if (schemas) {
        injectSchema(schemas, "schema-org");
      }
    }
    return () => removeSchema("schema-org");
  }, [fullPropertyData]);

  return (
    <>
      <Helmet>
          <title>
            {meta?.title}
          </title>
          <meta name='description' content={meta?.description} />
          <meta name='keywords' content={meta?.metaKeyWords} />
        </Helmet>
      <PropertyDetails />
    </>
  );
};

export default PdpPage;
